import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import BrandName from "../components/site/BrandName";
import { PriceReveal } from "../components/site/PriceReveal";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  Loader2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import {
  ACKNOWLEDGEMENTS,
  APPLY_STAGES,
  CITY_CHAPTERS,
  EMPTY_DRAFT,
  EXPERIENCE_RANGES,
  GENDERS,
  LOOKING_FOR_OPTIONS,
  MEMBERSHIP_BENEFITS,
  PRIMARY_CIRCLE_OPTIONS,
  PROFESSIONAL_TYPES,
  SECONDARY_CIRCLE_OPTIONS,
  formatDobInput,
  validateAcknowledgements,
  validateStage1,
  validateStage2,
} from "../lib/apply-schema";
import { trackLead, trackInitiateCheckout, trackPurchase } from "../lib/meta-pixel";
import InstagramProfileCard from "../components/site/InstagramProfileCard";

const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/** Lazily inject the Razorpay Checkout.js script exactly once. */
function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.Razorpay) return resolve(window.Razorpay);
    const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay")));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(window.Razorpay);
    s.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(s);
  });
}

const API_ROOT =
  (process.env.REACT_APP_BACKEND_URL || "") + "/api/applications";
const DRAFT_KEY = "hfa_apply_draft_id";
const LOCAL_STATE_KEY = "hfa_apply_local_state";
/** Drafts older than this are discarded on the next visit so a
 *  forgotten form from months ago cannot suddenly resurrect. */
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days

/** Read the persisted apply-page draft.
 *  Storage schema (v2):  { data: {...}, stage: 1|2|3, savedAt: <epoch ms> }
 *  Legacy schema (v1):   bare form-data object (auto-migrated on next save).
 *  Returns null when there is nothing usable to restore. */
function readLocalDraft() {
  try {
    const raw = localStorage.getItem(LOCAL_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // v2 envelope
    if (parsed.data && typeof parsed.data === "object") {
      const age = Date.now() - Number(parsed.savedAt || 0);
      if (Number.isFinite(age) && age > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(LOCAL_STATE_KEY);
        return null;
      }
      const stage = Number.isInteger(parsed.stage) && parsed.stage >= 1 && parsed.stage <= 3
        ? parsed.stage
        : 1;
      return { data: parsed.data, stage };
    }
    // v1 legacy — just the data object. Assume freshly-saved; no stage.
    return { data: parsed, stage: 1 };
  } catch (_) {
    return null;
  }
}

/* ============================================================ */
/*  Small primitive components                                   */
/* ============================================================ */

function Label({ children, required, htmlFor, aside }) {
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <label
        htmlFor={htmlFor}
        className="block text-sm font-semibold text-black tracking-tight"
      >
        {children}
        {required && <span className="text-orange-600 ml-1">*</span>}
      </label>
      {aside && <span className="text-xs text-black/50">{aside}</span>}
    </div>
  );
}

function Description({ children }) {
  return (
    <p className="text-[13px] text-black/60 mt-1.5 leading-relaxed">
      {children}
    </p>
  );
}

function HelpText({ children }) {
  return (
    <p className="text-xs text-black/55 mt-1.5 leading-relaxed">{children}</p>
  );
}

function ErrorText({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} data-testid={id} className="text-xs text-red-600 mt-1.5 font-medium">
      {children}
    </p>
  );
}

function TextInput({ id, value, onChange, placeholder, type = "text", testid, error, autoComplete = "off", inputMode, maxLength }) {
  return (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      maxLength={maxLength}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      aria-invalid={!!error}
      className={`w-full rounded-xl border ${
        error ? "border-red-400" : "border-black/12"
      } bg-white px-4 py-3.5 text-[15px] text-black placeholder:text-black/35 outline-none transition-all duration-200 focus:border-orange-600 focus:ring-4 focus:ring-orange-600/20 focus:shadow-[0_8px_24px_-16px_rgba(234,88,12,0.35)] hover:border-black/22`}
    />
  );
}

function TextArea({ id, value, onChange, placeholder, testid, rows = 4, error }) {
  return (
    <textarea
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      aria-invalid={!!error}
      className={`w-full rounded-xl border ${
        error ? "border-red-400" : "border-black/12"
      } bg-white px-4 py-3.5 text-[15px] text-black placeholder:text-black/35 outline-none transition-all duration-200 focus:border-orange-600 focus:ring-4 focus:ring-orange-600/20 focus:shadow-[0_8px_24px_-16px_rgba(234,88,12,0.35)] hover:border-black/22 resize-none`}
    />
  );
}

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Pill-style single-select. Long labels wrap gracefully. */
function PillGroup({ options, value, onChange, testidPrefix, columns = "grid-cols-1 sm:grid-cols-3" }) {
  return (
    <div className={`grid ${columns} gap-2.5`}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testidPrefix}-${slugify(opt).slice(0, 60)}`}
            onClick={() => onChange(opt)}
            aria-pressed={active}
            className={`group relative text-left rounded-xl border px-4 py-3.5 text-sm font-medium transition-all duration-300 ease-out will-change-transform ${
              active
                ? "border-orange-600 bg-orange-100/70 ring-1 ring-orange-600/25 text-black font-semibold shadow-[0_10px_24px_-14px_rgba(234,88,12,0.55)] -translate-y-[1px]"
                : "border-black/10 bg-white text-black/75 hover:border-black/25 hover:bg-black/[0.02] hover:-translate-y-[1px] hover:shadow-[0_6px_16px_-14px_rgba(0,0,0,0.35)]"
            }`}
          >
            <span className="flex items-center justify-between gap-3">
              <span className="min-w-0 flex-1 leading-snug">{opt}</span>
              <span
                className={`shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-all ${
                  active ? "border-orange-600 bg-orange-600" : "border-black/25"
                }`}
              >
                {active && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select checkbox group. */
function CheckboxGroup({ options, values, onChange, testidPrefix, columns = "grid-cols-1 sm:grid-cols-2" }) {
  const toggle = (opt) => {
    if (values.includes(opt)) onChange(values.filter((v) => v !== opt));
    else onChange([...values, opt]);
  };
  return (
    <div className={`grid ${columns} gap-2.5`}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            data-testid={`${testidPrefix}-${slugify(opt).slice(0, 60)}`}
            aria-pressed={active}
            className={`group flex items-start gap-3 rounded-xl border px-4 py-3.5 text-left text-sm transition-all duration-300 ease-out will-change-transform ${
              active
                ? "border-orange-600 bg-orange-100/70 ring-1 ring-orange-600/25 -translate-y-[1px] shadow-[0_10px_24px_-14px_rgba(234,88,12,0.5)]"
                : "border-black/10 bg-white hover:border-black/25 hover:bg-black/[0.02] hover:-translate-y-[1px] hover:shadow-[0_6px_16px_-14px_rgba(0,0,0,0.35)]"
            }`}
          >
            <span
              className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-md border transition-all ${
                active ? "border-orange-600 bg-orange-600" : "border-black/25"
              }`}
            >
              {active && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />}
            </span>
            <span className={`font-medium leading-snug ${active ? "text-black" : "text-black/75"}`}>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ============================================================ */
/*  Stepper — perfectly centered dots + equal connector lengths  */
/* ============================================================ */
function Stepper({ current }) {
  return (
    <div data-testid="apply-stepper" className="w-full">
      <div className="relative max-w-2xl mx-auto grid grid-cols-3">
        {/* Base rail — spans between the outer dots, aligned to dot center */}
        <div
          aria-hidden="true"
          className="absolute left-[16.6667%] right-[16.6667%] top-[7px] h-[2px] bg-black/10 rounded-full"
        />
        {/* Progress rail — grows in equal thirds as stages complete */}
        <motion.div
          aria-hidden="true"
          initial={false}
          animate={{
            width:
              current >= 3 ? "66.6667%" : current === 2 ? "33.3333%" : "0%",
          }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-[16.6667%] top-[7px] h-[2px] bg-orange-600 rounded-full"
        />
        {APPLY_STAGES.map((s) => {
          const done = current > s.id;
          const active = current === s.id;
          const state = done ? "done" : active ? "active" : "future";
          return (
            <div key={s.id} className="relative z-10 flex flex-col items-center min-w-0 px-2">
              <motion.div
                animate={{ scale: active ? 1.25 : done ? 1.05 : 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`apply-step-dot-${s.id}`}
                data-state={state}
                className={`relative w-4 h-4 rounded-full transition-colors duration-500 ${
                  done || active ? "bg-orange-600" : "bg-black/12"
                }`}
              >
                {active && (
                  <>
                    <span className="absolute -inset-1.5 rounded-full bg-orange-600/12" />
                    <span className="absolute inset-0 rounded-full ring-4 ring-orange-600/35 animate-pulse-soft" />
                  </>
                )}
              </motion.div>
              <div
                className={`mt-3.5 text-[11px] sm:text-xs uppercase tracking-[0.16em] text-center transition-colors duration-300 ${
                  active
                    ? "text-black font-bold"
                    : done
                    ? "text-black/80 font-semibold"
                    : "text-black/35 font-semibold"
                }`}
              >
                {s.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Stage 1 — Basic Details                                     */
/* ============================================================ */
function Stage1({ data, errors, update }) {
  return (
    <div className="space-y-10 md:space-y-12">
      <div>
        <Label required>Which City Chapter would you like to join?</Label>
        <div className="mt-3">
          <PillGroup
            options={CITY_CHAPTERS}
            value={data.city_chapter}
            onChange={(v) => update("city_chapter", v)}
            testidPrefix="apply-city-chapter"
            columns="grid-cols-2 sm:grid-cols-3"
          />
        </div>
        <ErrorText id="err-city_chapter">{errors.city_chapter}</ErrorText>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="full_name" required>Full Name</Label>
          <div className="mt-2">
            <TextInput
              id="full_name"
              value={data.full_name}
              onChange={(v) => update("full_name", v)}
              placeholder="Your full name"
              testid="apply-full-name"
              error={errors.full_name}
              autoComplete="name"
            />
          </div>
          <ErrorText id="err-full_name">{errors.full_name}</ErrorText>
        </div>

        <div>
          <Label required>Gender</Label>
          <div className="mt-2">
            <PillGroup
              options={GENDERS}
              value={data.gender}
              onChange={(v) => update("gender", v)}
              testidPrefix="apply-gender"
              columns="grid-cols-2"
            />
          </div>
          <ErrorText id="err-gender">{errors.gender}</ErrorText>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="date_of_birth" required aside="DD/MM/YYYY">
            Date of Birth
          </Label>
          <div className="mt-2">
            <TextInput
              id="date_of_birth"
              value={data.date_of_birth}
              onChange={(v) => update("date_of_birth", formatDobInput(v))}
              placeholder="DD/MM/YYYY"
              testid="apply-date-of-birth"
              inputMode="numeric"
              maxLength={10}
              error={errors.date_of_birth}
            />
          </div>
          <ErrorText id="err-date_of_birth">{errors.date_of_birth}</ErrorText>
        </div>

        <div>
          <Label htmlFor="whatsapp_number" required aside="(10-digit number)">
            WhatsApp Number
          </Label>
          <div className="mt-2">
            {/* Fixed +91 prefix — the merchant is India-only and this
                exactly matches what Razorpay expects as prefill.contact.
                The prefix is decorative; only the 10-digit local number
                is stored in state (data.whatsapp_number). */}
            <div
              className={`flex items-stretch w-full rounded-xl border ${
                errors.whatsapp_number ? "border-red-400" : "border-black/12"
              } bg-white overflow-hidden transition-all duration-200 focus-within:border-orange-600 focus-within:ring-4 focus-within:ring-orange-600/20 focus-within:shadow-[0_8px_24px_-16px_rgba(234,88,12,0.35)] hover:border-black/22`}
            >
              <span
                aria-hidden="true"
                data-testid="apply-whatsapp-prefix"
                className="flex items-center px-3.5 text-[15px] font-medium text-black/60 bg-black/[0.03] border-r border-black/10 select-none"
              >
                +91
              </span>
              <input
                id="whatsapp_number"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
                value={data.whatsapp_number ?? ""}
                onChange={(e) =>
                  update(
                    "whatsapp_number",
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
                onPaste={(e) => {
                  // Strip anything that looks like a country code from a
                  // pasted value so "+91 98765 43210" or "919876543210"
                  // both collapse to the trailing 10 digits.
                  e.preventDefault();
                  const digits = (e.clipboardData.getData("text") || "")
                    .replace(/\D/g, "");
                  const local = digits.length > 10 ? digits.slice(-10) : digits;
                  update("whatsapp_number", local.slice(0, 10));
                }}
                placeholder="XXXXXXXXXX"
                data-testid="apply-whatsapp"
                aria-invalid={!!errors.whatsapp_number}
                className="flex-1 min-w-0 bg-transparent px-4 py-3.5 text-[15px] text-black placeholder:text-black/35 outline-none tracking-[0.02em]"
              />
            </div>
          </div>
          <HelpText>This number will be used for your membership and community access.</HelpText>
          <ErrorText id="err-whatsapp_number">{errors.whatsapp_number}</ErrorText>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="current_city" required>Current City</Label>
          <div className="mt-2">
            <TextInput
              id="current_city"
              value={data.current_city}
              onChange={(v) => update("current_city", v)}
              placeholder=""
              testid="apply-current-city"
              autoComplete="address-level2"
              error={errors.current_city}
            />
          </div>
          <ErrorText id="err-current_city">{errors.current_city}</ErrorText>
        </div>

        <div>
          <Label htmlFor="email" required>Email Address</Label>
          <div className="mt-2">
            <TextInput
              id="email"
              type="email"
              value={data.email}
              onChange={(v) => update("email", v)}
              placeholder="you@example.com"
              testid="apply-email"
              autoComplete="email"
              error={errors.email}
            />
          </div>
          <ErrorText id="err-email">{errors.email}</ErrorText>
          <HelpText>
            <span data-testid="apply-email-help">A confirmation email will be sent to this email address.</span>
          </HelpText>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="linkedin_url">LinkedIn Profile</Label>
          <div className="mt-2">
            <TextInput
              id="linkedin_url"
              value={data.linkedin_url}
              onChange={(v) => update("linkedin_url", v)}
              placeholder="https://linkedin.com/in/…"
              testid="apply-linkedin"
              autoComplete="url"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="instagram_username">Instagram ID</Label>
          <div className="mt-2">
            <TextInput
              id="instagram_username"
              value={data.instagram_username}
              onChange={(v) => update("instagram_username", v)}
              placeholder="@yourhandle"
              testid="apply-instagram"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Stage 2 — Professional Details                               */
/* ============================================================ */
function Stage2({ data, errors, update }) {
  const showOther = data.professional_type === "Other";
  return (
    <div className="space-y-10 md:space-y-12">
      <div>
        <Label required>Which best describes you?</Label>
        <div className="mt-3">
          <PillGroup
            options={PROFESSIONAL_TYPES}
            value={data.professional_type}
            onChange={(v) => {
              update("professional_type", v);
              if (v !== "Other") update("professional_type_other", "");
            }}
            testidPrefix="apply-professional-type"
            columns="grid-cols-1 sm:grid-cols-3"
          />
        </div>
        <AnimatePresence initial={false}>
          {showOther && (
            <motion.div
              key="other-input"
              initial={{ opacity: 0, y: -6, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -6, height: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-3">
                <TextInput
                  id="professional_type_other"
                  value={data.professional_type_other}
                  onChange={(v) => update("professional_type_other", v)}
                  placeholder="Please specify"
                  testid="apply-professional-type-other-input"
                  error={errors.professional_type_other}
                />
                <ErrorText id="err-professional_type_other">{errors.professional_type_other}</ErrorText>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <ErrorText id="err-professional_type">{errors.professional_type}</ErrorText>
      </div>

      <div>
        <Label required>
          Choose the Professional Circle that best matches what you primarily do (Primary Professional Circle)
        </Label>
        <Description>
          Every member is also part of their City Members Group, where professionals from all Professional Circles connect. Choose the Professional Circle that best matches what you primarily do. If your work doesn&rsquo;t fit any of the listed circles, simply select &ldquo;None of the below – Join City Members Group only.&rdquo;
        </Description>
        <div className="mt-4">
          <PillGroup
            options={PRIMARY_CIRCLE_OPTIONS}
            value={data.primary_circle}
            onChange={(v) => update("primary_circle", v)}
            testidPrefix="apply-primary-circle"
            columns="grid-cols-1 sm:grid-cols-2"
          />
        </div>
        <ErrorText id="err-primary_circle">{errors.primary_circle}</ErrorText>
      </div>

      <div>
        <Label htmlFor="professional_description" required>What do you do professionally?</Label>
        <div className="mt-2">
          <TextArea
            id="professional_description"
            value={data.professional_description}
            onChange={(v) => update("professional_description", v)}
            placeholder="Briefly describe your current role, business, or work in a line or two."
            testid="apply-professional-description"
            rows={3}
            error={errors.professional_description}
          />
        </div>
        <ErrorText id="err-professional_description">{errors.professional_description}</ErrorText>
      </div>

      <div>
        <Label required>How long have you been working in this domain?</Label>
        <div className="mt-3">
          <PillGroup
            options={EXPERIENCE_RANGES}
            value={data.experience_range}
            onChange={(v) => update("experience_range", v)}
            testidPrefix="apply-experience"
            columns="grid-cols-2 sm:grid-cols-5"
          />
        </div>
        <ErrorText id="err-experience_range">{errors.experience_range}</ErrorText>
      </div>

      <div>
        <Label required>
          Does your work also relate to another Professional Circle? (Secondary Professional Circle)
        </Label>
        <div className="mt-3">
          <PillGroup
            options={SECONDARY_CIRCLE_OPTIONS}
            value={data.secondary_circle}
            onChange={(v) => update("secondary_circle", v)}
            testidPrefix="apply-secondary-circle"
            columns="grid-cols-1 sm:grid-cols-2"
          />
        </div>
        <ErrorText id="err-secondary_circle">{errors.secondary_circle}</ErrorText>
      </div>

      <div>
        <Label htmlFor="secondary_description" required>
          If you&rsquo;ve selected a Secondary Professional Circle above, briefly describe what you do in that field.
        </Label>
        <div className="mt-2">
          <TextArea
            id="secondary_description"
            value={data.secondary_description}
            onChange={(v) => update("secondary_description", v)}
            placeholder="Briefly describe your work in the secondary circle."
            testid="apply-secondary-description"
            rows={3}
            error={errors.secondary_description}
          />
        </div>
        <HelpText>If not applicable, simply write &ldquo;NA&rdquo;.</HelpText>
        <ErrorText id="err-secondary_description">{errors.secondary_description}</ErrorText>
      </div>

      <div>
        <Label required>What are you currently looking for?</Label>
        <div className="mt-3">
          <CheckboxGroup
            options={LOOKING_FOR_OPTIONS}
            values={data.looking_for || []}
            onChange={(vs) => update("looking_for", vs)}
            testidPrefix="apply-looking-for"
          />
        </div>
        <HelpText>Select all that apply.</HelpText>
        <ErrorText id="err-looking_for">{errors.looking_for}</ErrorText>
      </div>

      <div>
        <Label htmlFor="notes">
          Any questions, details, or suggestions you&rsquo;d like to share with us?
        </Label>
        <div className="mt-2">
          <TextArea
            id="notes"
            value={data.notes}
            onChange={(v) => update("notes", v)}
            placeholder="Anything else you'd like us to know."
            testid="apply-notes"
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Acknowledgements — 4 required checkboxes                     */
/* ============================================================ */
function AcknowledgementCard({ acknowledgements, onChange, error }) {
  const toggle = (key) => {
    onChange({ ...acknowledgements, [key]: !acknowledgements[key] });
  };
  return (
    <div
      data-testid="apply-acknowledgement-card"
      className={`rounded-3xl border ${
        error ? "border-red-300" : "border-black/10"
      } bg-white p-6 md:p-8`}
    >
      <div className="text-[11px] uppercase tracking-[0.24em] text-orange-600 font-semibold flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" />
        Application Acknowledgements
      </div>
      <p className="mt-3 text-sm text-black/60">
        Please review and confirm the following to submit your application.
      </p>
      <ul className="mt-5 space-y-3">
        {ACKNOWLEDGEMENTS.map((item) => {
          const active = !!acknowledgements[item.key];
          return (
            <li key={item.key}>
              <button
                type="button"
                onClick={() => toggle(item.key)}
                data-testid={`apply-ack-${item.key}`}
                aria-pressed={active}
                className={`w-full flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-left text-sm transition-all duration-200 ${
                  active
                    ? "border-orange-600 bg-orange-50"
                    : "border-black/10 bg-white hover:border-black/25"
                }`}
              >
                <span
                  className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-md border transition-all ${
                    active ? "border-orange-600 bg-orange-600" : "border-black/25"
                  }`}
                >
                  {active && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />}
                </span>
                <span className={`font-medium leading-snug ${active ? "text-black" : "text-black/75"}`}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <ErrorText id="err-acknowledgements">{error}</ErrorText>
    </div>
  );
}

/* ============================================================ */
/*  Stage 3 — Membership Summary                                 */
/* ============================================================ */
function Stage3({ data, ackError, updateAck }) {
  return (
    <div className="space-y-12 md:space-y-16">
      <div className="max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.24em] text-orange-600 font-semibold flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          Membership Summary
        </div>
        <h2
          data-testid="apply-stage3-heading"
          className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest leading-[1.05]"
        >
          Curated Intentionally
        </h2>
        <p className="mt-5 text-[15px] md:text-lg text-black/70 leading-relaxed">
          High Five Access is built for professionals who value meaningful, relevant connections over a random crowd.
        </p>
        <p className="mt-4 text-[15px] md:text-lg text-black/70 leading-relaxed">
          Every application is reviewed to help build a curated community of people who are here to connect, contribute, and grow through meaningful professional relationships.
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div
          data-testid="apply-membership-card"
          className="relative rounded-3xl border border-white/10 bg-[#0A0A0A] text-white p-7 md:p-10 overflow-hidden"
        >
          <div
            aria-hidden="true"
            className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-orange-600/25 blur-3xl pointer-events-none max-sm:will-change-transform max-sm:[transform:translateZ(0)]"
          />
          <div className="flex items-center gap-2 text-orange-500 text-[14px] md:text-[15px] uppercase tracking-[0.22em] font-semibold">
            <Sparkles className="w-4 h-4" />
            Annual Membership
          </div>
          <div className="mt-6">
            <PriceReveal variant="card" testid="apply-pricing" />
          </div>
          <p className="mt-3 text-[13px] md:text-sm text-white/60">
            Valid for 12 months from the date of joining.
          </p>
          <div className="hairline-dark my-8" />
          <ul className="space-y-3.5">
            {MEMBERSHIP_BENEFITS.map((b, i) => (
              <li
                key={b}
                data-testid={`apply-benefit-${i}`}
                className="flex items-start gap-3 text-[15px] text-white/85 leading-relaxed"
              >
                <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-orange-600/20 text-orange-500 flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-white/85 leading-relaxed">
            Participation fees for offline networking meetups may vary depending on the format and venue.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full">
        <AcknowledgementCard
          acknowledgements={data.acknowledgements || {}}
          onChange={updateAck}
          error={ackError}
        />
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Success screen                                               */
/* ============================================================ */
/** Brief post-payment handoff screen — rendered immediately after the
 *  server-side `/payment/verify` returns a paid response and just
 *  before the existing `<SuccessScreen />`. Purpose: when the
 *  applicant returns from an external payment surface (Google Pay,
 *  UPI app, banking app, Razorpay iframe on a switched tab, etc.),
 *  they need one clear beat to know they're back on the right screen
 *  and their confirmation is coming next. Deliberately calm and
 *  minimal — a soft orange ring around a live seconds count and a
 *  single readable line. No "redirect" language, no CTA, no dismissal
 *  — the countdown auto-advances to the SuccessScreen (see the timer
 *  effect in <Apply/>). */
function PostPaymentTransition({ seconds }) {
  const secondsLabel = seconds === 1 ? "1 second" : `${seconds} seconds`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid="apply-post-payment-transition"
      className="max-w-2xl mx-auto text-center px-4"
    >
      <div className="relative inline-flex items-center justify-center w-16 h-16">
        {/* Static ring — the muted outline sits beneath the animated
            ring so the countdown looks like a soft progress indicator
            rather than a spinner emergency. */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border-2 border-orange-600/15"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border-2 border-orange-600 border-t-transparent animate-spin"
          style={{ animationDuration: "1.4s" }}
        />
        <span
          data-testid="apply-post-payment-count"
          className="relative font-display text-2xl font-bold text-orange-600"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {seconds}
        </span>
      </div>
      <p
        data-testid="apply-post-payment-message"
        className="mt-8 text-base md:text-lg text-black/75 leading-relaxed max-w-md mx-auto"
      >
        Please don&rsquo;t close or exit this screen. Your confirmation will
        appear in {secondsLabel}.
      </p>
    </motion.div>
  );
}

function SuccessScreen() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="apply-success"
      className="max-w-2xl mx-auto text-center"
    >
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-600 text-white shadow-[0_16px_40px_-12px_rgba(234,88,12,0.55)]">
        <Check className="w-8 h-8" strokeWidth={3} />
      </div>
      <h1
        data-testid="apply-success-headline"
        className="font-display mt-8 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest leading-[1.05]"
      >
        Thank you for applying to High Five Access
      </h1>
      <p className="mt-5 text-base md:text-lg text-black/70 leading-relaxed">
        Your application has been received successfully.
      </p>
      <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed">
        Since every application is reviewed individually, we&rsquo;ll get back to you on WhatsApp within 2 working days with the next update and onboarding details.
      </p>
      <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed">
        Thank you for your patience. We look forward to welcoming you to High Five Access!
      </p>

      <InstagramProfileCard className="mt-10" testid="apply-success-instagram" />

      <div className="mt-10">
        <Link
          to="/"
          data-testid="apply-success-back-home"
          className="text-sm text-black/60 hover:text-black transition-colors inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to <BrandName /></span>
        </Link>
      </div>
    </motion.div>
  );
}

/* ============================================================ */
/*  Main Apply page                                              */
/* ============================================================ */
export default function Apply() {
  const [stage, setStage] = useState(1);
  const [data, setData] = useState(() => {
    const restored = readLocalDraft();
    return restored ? { ...EMPTY_DRAFT, ...restored.data } : { ...EMPTY_DRAFT };
  });
  const [errors, setErrors] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState({ done: false, applicationId: null, phase: null });
  // Post-payment transition state. `phase === "transition"` shows the
  // brief handoff screen; once the countdown reaches zero we flip to
  // `phase === "final"` and the existing SuccessScreen renders. The
  // countdown is a separate piece of state so the tick effect can
  // rerun without re-running the verify handler.
  const [transitionSeconds, setTransitionSeconds] = useState(5);
  const [initError, setInitError] = useState(null);

  const saveTimer = useRef(null);
  const stateRef = useRef(data);
  stateRef.current = data;

  /* --- Boot: hydrate from localStorage only (no backend write) ----
   * The application document is created in Atlas ONLY when the user
   * clicks the final CTA ("Become a Member"). Until then everything
   * lives in localStorage — Atlas never sees Stage 1 / Stage 2 / Summary
   * traffic, so it only ever contains genuine applicants who intended
   * to pay. Drafts older than 30 days are silently discarded so a
   * forgotten form from months ago cannot suddenly resurrect. */
  useEffect(() => {
    const restored = readLocalDraft();
    if (restored) {
      const merged = { ...EMPTY_DRAFT };
      Object.keys(EMPTY_DRAFT).forEach((k) => {
        if (restored.data[k] !== undefined && restored.data[k] !== null) {
          merged[k] = restored.data[k];
        }
      });
      setData(merged);
      // Only restore a non-initial stage when there is actual form
      // content — otherwise a spurious empty draft would land users on
      // Stage 3 with nothing filled in.
      const hasContent = Object.values(merged).some((v) => {
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "string") return v.trim().length > 0;
        if (typeof v === "boolean") return v;
        return false;
      });
      if (hasContent && restored.stage > 1) setStage(restored.stage);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
  }, []);

  /* --- Scroll to top on every stage change -----------------------
   * Runs AFTER React commits the new stage so the target section is
   * already in the DOM before we scroll. This guarantees every step
   * begins from its heading (both desktop + mobile), without any
   * layout-shift race that requestAnimationFrame-inline calls had. */
  const isFirstStageMount = useRef(true);
  useEffect(() => {
    if (isFirstStageMount.current) {
      isFirstStageMount.current = false;
      return;
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (e) {
      try { window.scrollTo(0, 0); } catch (_) { /* noop */ }
    }
  }, [stage]);

  /* --- Autosave: localStorage only (no backend writes) ----------- */
  const scheduleAutosave = useCallback(() => {
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // localStorage is written in the effect below on every change; this
      // timer just clears the transient "Saving…" flag and marks the
      // "saved once" state so the UI messaging stays honest.
      setSavedOnce(true);
      setSaving(false);
    }, 400);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    // Once payment is verified we treat the applicant as fully
    // onboarded. Skip writing autosave frames after this point so
    // (a) the removeItem call in the payment handler cannot be
    //     accidentally undone by a trailing render, and
    // (b) if the applicant refreshes the success screen and lands
    //     back on Stage 1, we never re-populate the empty form.
    if (submitted.done) return;
    try {
      // v2 envelope: bundle current data, current stage and a timestamp
      // so we can (a) restore the applicant to the exact step they were
      // on and (b) expire drafts that have been sitting for over 30 days.
      localStorage.setItem(
        LOCAL_STATE_KEY,
        JSON.stringify({ data, stage, savedAt: Date.now() })
      );
    } catch (e) { /* noop */ }
    scheduleAutosave();
  }, [data, stage, draftId, hydrated, submitted.done, scheduleAutosave]);

  /* --- Post-payment transition countdown -------------------------
   * Ticks the visible seconds counter down from 5 → 1 while the app
   * sits in `phase === "transition"`, then flips to `phase = "final"`
   * so <SuccessScreen /> renders. Only runs while the transition is
   * active; the effect cleans up its timer on unmount and on every
   * `transitionSeconds` change so we can never end up with two
   * concurrent tickers (spec §8). The verify handler resets the
   * counter to 5 immediately before setting phase, so a re-render
   * from the same submitted state never restarts the countdown at
   * the wrong number.
   */
  useEffect(() => {
    if (!submitted.done || submitted.phase !== "transition") return undefined;
    if (transitionSeconds <= 1) {
      const id = setTimeout(() => {
        setSubmitted((prev) =>
          prev.done && prev.phase === "transition"
            ? { ...prev, phase: "final" }
            : prev
        );
      }, 1000);
      return () => clearTimeout(id);
    }
    const id = setTimeout(
      () => setTransitionSeconds((n) => (n > 1 ? n - 1 : n)),
      1000
    );
    return () => clearTimeout(id);
  }, [submitted.done, submitted.phase, transitionSeconds]);

  /* --- Field update helper --------------------------------------- */
  const update = useCallback((key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const updateAck = useCallback((nextAck) => {
    setData((prev) => ({ ...prev, acknowledgements: nextAck }));
    setErrors((prev) => {
      if (!prev.acknowledgements) return prev;
      const next = { ...prev };
      delete next.acknowledgements;
      return next;
    });
  }, []);

  /* --- Stage transitions ----------------------------------------- */
  const goNext = () => {
    let errs = {};
    if (stage === 1) errs = validateStage1(data);
    if (stage === 2) errs = validateStage2(data);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      requestAnimationFrame(() => {
        const el =
          document.getElementById(firstKey) ||
          document.querySelector(`[data-testid^="apply-${firstKey.replace(/_/g, "-")}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setStage((s) => Math.min(3, s + 1));
  };

  const goPrev = () => {
    setStage((s) => Math.max(1, s - 1));
  };

  /* --- Submission ------------------------------------------------ */
  const allAcked = useMemo(() => {
    const a = data.acknowledgements || {};
    return ACKNOWLEDGEMENTS.every((it) => !!a[it.key]);
  }, [data.acknowledgements]);

  const handleSubmit = async () => {
    const e1 = validateStage1(data);
    const e2 = validateStage2(data);
    const eAck = validateAcknowledgements(data.acknowledgements);
    const all = { ...e1, ...e2, ...eAck };
    if (Object.keys(all).length > 0) {
      setErrors(all);
      if (Object.keys(e1).length > 0) setStage(1);
      else if (Object.keys(e2).length > 0) setStage(2);
      return;
    }
    setSubmitting(true);
    setErrors((prev) => {
      const { __submit__, ...rest } = prev;
      return rest;
    });

    try {
      // 1) Create the application document in Atlas (status='unpaid').
      //    This is the FIRST time we write to the database — nothing
      //    was persisted server-side while filling Steps 1 / 2 / Summary.
      const finalizeRes = await axios.post(`${API_ROOT}/finalize`, {
        ...data,
      });
      const appUuid = finalizeRes.data.id;
      const applicationId = finalizeRes.data.application_id;

      // Meta Pixel — Lead: the application is now real in Atlas.
      // Deterministic event_id (`lead_<uuid>`) means a page refresh, a
      // retry, or a resumed session cannot double-count this Lead.
      trackLead({ appUuid });

      // 2) Create the Razorpay Order for that application.
      const orderRes = await axios.post(
        `${API_ROOT}/${appUuid}/payment/order`
      );
      const order = orderRes.data;

      // Meta Pixel — InitiateCheckout: a valid Razorpay order exists.
      // event_id ties the event to the specific order id, so if the
      // user abandons and later comes back with a brand-new order,
      // that new order legitimately produces a fresh InitiateCheckout.
      trackInitiateCheckout({
        appUuid,
        orderId: order.order_id,
        amountPaise: order.amount_paise,
        currency: order.currency,
      });

      // 3) Load Razorpay Checkout.js on demand.
      let RazorpayCtor;
      try {
        RazorpayCtor = await loadRazorpay();
      } catch (e) {
        setErrors({ __submit__: "Could not open payment window. Please check your connection and try again." });
        return;
      }

      // 4) Open the Razorpay checkout modal.
      //    Prefill ALL three fields — name, email, contact — so the
      //    checkout skips its "Contact Info" screen entirely and lands
      //    directly on the payment-method picker.
      //
      //    Contact format: Razorpay's own docs recommend E.164 with the
      //    country code prefix (e.g. "+919876543210"). On mobile
      //    devices Razorpay Checkout is strict — a plain 10-digit
      //    string is often rejected and the applicant is prompted to
      //    re-enter their number. We normalise to "+91" + last-10
      //    digits (India-only merchant, all city chapters are Indian)
      //    which is universally accepted across desktop Chrome, mobile
      //    Chrome and mobile Safari.
      const serverPrefill = order.prefill || {};
      const rawContact = String(
        data.whatsapp_number || serverPrefill.contact || ""
      ).trim();
      // Strip everything except digits, then keep the last 10 digits
      // (defensive against pasted country-coded numbers like +91XXXX or
      // 91XXXX). Prepend "+91" so Razorpay sees a canonical E.164
      // Indian number.
      const digitsOnly = rawContact.replace(/\D/g, "");
      const tenDigits = digitsOnly.length >= 10 ? digitsOnly.slice(-10) : digitsOnly;
      const prefillContact = tenDigits.length === 10 ? `+91${tenDigits}` : tenDigits;
      const prefill = {
        name: (data.full_name || serverPrefill.name || "").trim(),
        email: (data.email || serverPrefill.email || "").trim(),
        contact: prefillContact,
      };

      const rzp = new RazorpayCtor({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "High Five Access",
        description: order.description || "Annual Membership",
        order_id: order.order_id,
        prefill,
        theme: { color: "#EA580C" },
        modal: {
          ondismiss: () => {
            // Application stays in Atlas with status='unpaid'. The user
            // can re-open /apply and complete payment later without
            // losing anything they entered.
            setSubmitting(false);
          },
        },
        handler: async (response) => {
          // 5) Verify on server — status flips to 'pending_review'.
          try {
            const verifyRes = await axios.post(
              `${API_ROOT}/${appUuid}/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );
            const appId = verifyRes.data?.application_id || applicationId;

            // Meta Pixel — Purchase. The backend is the SOURCE OF
            // TRUTH: only fire when /payment/verify explicitly
            // confirms `ok=true` and `payment_status=paid`. Anything
            // else (signature failure, network error, non-paid
            // response) skipped this branch via the catch below.
            //
            // Duplicate prevention: event_id is
            // `purchase_<razorpay_payment_id>`. A Razorpay payment id
            // is globally unique per successful transaction, and
            // `trackPurchase` keys off this id in localStorage — a
            // browser refresh, re-open, back/forward, or re-render of
            // this handler can never fire a second Purchase for the
            // same underlying transaction.
            if (
              verifyRes.data?.ok === true &&
              verifyRes.data?.payment_status === "paid"
            ) {
              trackPurchase({
                razorpayPaymentId: response.razorpay_payment_id,
                value: 1999,
                currency: "INR",
              });
            }

            // Wipe every trace of the completed draft. This is the ONLY
            // place the draft is cleared automatically — payment cancel
            // / failure / modal dismiss all leave it intact so the
            // applicant can safely resume. Order matters: flip
            // submitted.done FIRST so the autosave effect short-circuits
            // before it observes the (now empty) state, then reset the
            // React form state, then remove the persisted envelope.
            //
            // Confirmation is shown immediately once the server's
            // /payment/verify explicitly returned ok=true with
            // payment_status="paid" — never merely because Razorpay's
            // client-side callback fired. (The former 5-second
            // transition card was removed by request.)
            setTransitionSeconds(5);
            setSubmitted({ done: true, applicationId: appId, phase: "final" });
            setData({ ...EMPTY_DRAFT });
            setStage(1);
            setDraftId(null);
            setErrors({});
            try { localStorage.removeItem(LOCAL_STATE_KEY); } catch (_) { /* noop */ }
            requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
          } catch (e) {
            const msg = e?.response?.data?.detail || "Payment received but we couldn't finalize your application. Please contact us on WhatsApp.";
            setErrors({ __submit__: msg });
          } finally {
            setSubmitting(false);
          }
        },
      });

      rzp.on("payment.failed", (resp) => {
        const msg = resp?.error?.description || "Payment failed. Please try again or use a different method.";
        setErrors({ __submit__: msg });
        setSubmitting(false);
      });

      rzp.open();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors({ __submit__: msg });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans hfa-apply-root">
      {/* Viewport stability styles — scoped to the Apply page only.
       *  1) Ensures every focusable form control on mobile has font-size
       *     >= 16px, which is what iOS Safari uses as the threshold to
       *     auto-zoom on focus. Without this rule, tapping any input on
       *     an iPhone visibly zooms the page (and never zooms back on
       *     blur), which the user asked us to eliminate.
       *  2) Neutralises the WebKit/Firefox mobile "text-size-adjust"
       *     behaviour that occasionally rescales body copy in Android
       *     Chrome and iOS Safari after a rotate/resume, causing the
       *     small layout shift the user described.
       *  3) Preserves accessibility — pinch-to-zoom is NOT disabled;
       *     we only stop the browser's automatic zoom/rescale. */}
      <style>{`
        .hfa-apply-root {
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }
        @media (max-width: 640px) {
          .hfa-apply-root input,
          .hfa-apply-root select,
          .hfa-apply-root textarea {
            font-size: 16px !important;
          }
        }
      `}</style>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/5">
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="apply-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display font-bold text-base md:text-lg tracking-tight text-black">
              <BrandName />
            </span>
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-black/45 font-semibold hidden sm:block">
            Membership Application
          </div>
        </div>
      </header>

      <main className="container-x pt-8 md:pt-10 pb-20 md:pb-28">
        {submitted.done ? (
          submitted.phase === "transition" ? (
            <PostPaymentTransition seconds={transitionSeconds} />
          ) : (
            <SuccessScreen />
          )
        ) : (
          <div className="max-w-5xl xl:max-w-6xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <h1
                data-testid="apply-headline"
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tightest leading-[1.05] mx-auto"
              >
                Join a curated professional community.
              </h1>
              <p className="mt-4 md:mt-5 text-base md:text-lg text-black/65 leading-relaxed">
                A short, thoughtful application.
              </p>
              <div
                data-testid="apply-form-header-note"
                className="mt-5 md:mt-6 flex items-center justify-center gap-2 flex-wrap text-xs text-black/55"
              >
                <span className={`inline-flex items-center gap-1.5 ${saving ? "text-black/50" : "text-green-700"}`}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  {saving ? "Saving…" : "Auto-saves as you go"}
                </span>
              </div>
            </div>

            <div className="mt-10 md:mt-12">
              <Stepper current={stage} />
            </div>

            {initError && (
              <div
                data-testid="apply-init-error"
                className="mt-10 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3"
              >
                {initError}
              </div>
            )}

            <div className="mt-8 md:mt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={stage}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {stage === 1 && <Stage1 data={data} errors={errors} update={update} />}
                  {stage === 2 && <Stage2 data={data} errors={errors} update={update} />}
                  {stage === 3 && (
                    <Stage3
                      data={data}
                      ackError={errors.acknowledgements}
                      updateAck={updateAck}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {errors.__submit__ && (
              <div
                data-testid="apply-submit-error"
                className="mt-8 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3"
              >
                {errors.__submit__}
              </div>
            )}

            <div className="mt-12 md:mt-16 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              {stage > 1 ? (
                <button
                  type="button"
                  onClick={goPrev}
                  data-testid="apply-previous"
                  className="btn-outline w-full sm:w-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>
              ) : (
                <span />
              )}

              {stage < 3 ? (
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="apply-next"
                  className="btn-primary w-full sm:w-auto"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  aria-disabled={submitting || !allAcked}
                  data-testid="apply-submit"
                  className={`btn-primary w-full sm:w-auto ${
                    submitting || !allAcked ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Opening secure payment…
                    </>
                  ) : (
                    <>
                      Become a Member
                      <ArrowUpRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-black/5 bg-white py-8">
        <div className="container-x text-center sm:text-left text-xs text-black/45">
          © {new Date().getFullYear()} <BrandName />. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
